import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthStore } from '@features/authentication/state/auth.store';
import { SessionService } from '@core/services/session/session.service';
import { catchError, EMPTY, throwError } from 'rxjs';

// Endpoints públicos de autenticación: un 401 aquí es un rechazo de
// credenciales/token puntual, no una expiración de sesión activa, así que
// deben quedar fuera del manejo automático para evitar bucles de logout.
const AUTH_ENDPOINT_PATTERNS = [
  'v1/auth/login',
  'v1/end_user/auth/register',
  'v1/end_user/auth/continue-with-email',
  'v1/end_user/auth/forgot-password',
  'v1/end_user/auth/reset-password',
  'v1/end_user/auth/verify-token',
];

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINT_PATTERNS.some((pattern) => url.includes(pattern));
}

// Flag a nivel de módulo (el interceptor es una función singleton): evita
// que ráfagas de 401 simultáneas (varias requests en vuelo cuando el token
// expira) disparen handleSessionExpired más de una vez.
let isHandlingSessionExpiration = false;

export const authErrorInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore);
  const session = inject(SessionService);

  return next(req).pipe(
    catchError((err: HttpErrorResponse) => {
      const isAuthError = err.status === 401;
      // isIdentified cubre 'authenticated' Y 'loading': un token persistido
      // que falla en la primera verificación al arrancar la app (afterNextRender
      // -> verifySession) es una expiración tan real como una que ocurre en
      // sesión ya confirmada, y debe pasar por el mismo manejo diferenciado.
      const hasActiveSession = authStore.isIdentified();

      if (isAuthError && hasActiveSession && !isAuthEndpoint(req.url)) {
        if (!isHandlingSessionExpiration) {
          isHandlingSessionExpiration = true;
          try {
            session.handleSessionExpired();
          } finally {
            isHandlingSessionExpiration = false;
          }
        }
        return EMPTY;
      }
      return throwError(() => err);
    }),
  );
};
